import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { ManageHeader, SearchBar, Fab, EmptyState, Field, TextInput, TextArea, SelectInput, Toggle } from '../../components/mobile/ManageKit';
import { DatePicker } from '../../components/ui/date-picker';
import { HardHat, Trash2, Loader2 } from 'lucide-react';

const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const empty = {
  name: '', phone: '', email: '', role: 'operator', license_number: '', license_expiry: '',
  supplier_id: '', suburb: '', state: '', is_available: true, worker_type_ids: [],
};

const MobileManageOperators = () => {
  const { api, can } = useMobileAuth();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ops, sup, wt] = await Promise.all([
        api.get('/api/operators'),
        api.get('/api/suppliers'),
        api.get('/api/worker-types').catch(() => ({ data: [] })),
      ]);
      setItems(ops.data || []);
      setSuppliers(sup.data || []);
      setWorkerTypes(wt.data || []);
    } catch (e) {
      toast.error('Failed to load operators');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setConfirmDel(false); setOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...empty, ...Object.fromEntries(Object.entries(o).filter(([, v]) => v != null)) }); setConfirmDel(false); setOpen(true); };

  const save = async () => {
    if (!(form.name || '').trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone || null,
        email: form.email || null,
        role: form.role,
        license_number: form.license_number || null,
        license_expiry: form.license_expiry || null,
        supplier_id: form.supplier_id || null,
        suburb: form.suburb || null,
        state: form.state || null,
        is_available: form.is_available,
        worker_type_ids: form.worker_type_ids || [],
        // Preserve existing mobile/skills fields on edit (this form doesn't manage them)
        equipment_types: editing?.equipment_types || [],
        mobile_enabled: editing?.mobile_enabled || false,
        mobile_licence_type: editing?.mobile_licence_type || 'full',
      };
      if (editing) await api.put(`/api/operators/${editing.id}`, payload);
      else await api.post('/api/operators', payload);
      toast.success(editing ? 'Operator updated' : 'Operator added');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/api/operators/${editing.id}`);
      toast.success('Operator deleted');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cannot delete');
    }
  };

  const filtered = items.filter((o) => o.name?.toLowerCase().includes(q.toLowerCase()));

  return (
    <MobileLayout>
      <ManageHeader title="Operators" subtitle={`${items.length} total`} />
      <div className="p-4 space-y-3">
        <SearchBar value={q} onChange={setQ} placeholder="Search operators..." />
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 text-[#ffd108] animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No operators" subtitle="Tap Add to create one" />
        ) : (
          <div className="space-y-2" data-testid="operators-list">
            {filtered.map((o) => (
              <button key={o.id} onClick={() => openEdit(o)}
                className="w-full flex items-center gap-3 bg-[var(--m-bg-card)] border border-[var(--m-border)] rounded-lg px-4 py-3 text-left active:bg-[var(--m-bg-elevated)]"
                data-testid={`operator-row-${o.id}`}>
                <div className="w-9 h-9 rounded-lg bg-[var(--m-bg)] flex items-center justify-center shrink-0">
                  <HardHat className="h-5 w-5 text-[#ffd108]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--m-text)] font-medium truncate">{o.name}</div>
                  <div className="text-xs text-[var(--m-text-secondary)] truncate capitalize">{o.role}{o.phone ? ` · ${o.phone}` : ''}</div>
                </div>
                {o.mobile_enabled && <span className="text-[10px] font-bold uppercase text-[#ffd108] bg-[#ffd108]/10 px-2 py-0.5 rounded-full">Mobile</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <Fab onClick={openNew} label="Add" testId="operator-add-fab" visible={can('operators.write')} />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="bg-[var(--m-bg)] border-[var(--m-border)] rounded-t-2xl max-h-[90vh] overflow-y-auto">
          <SheetHeader><SheetTitle className="text-[var(--m-text)] text-left">{editing ? 'Edit Operator' : 'New Operator'}</SheetTitle></SheetHeader>
          <div className="py-4 space-y-3">
            <Field label="Full name" required><TextInput testId="op-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith" /></Field>
            <Field label="Role">
              <SelectInput testId="op-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder=""
                options={[{ value: 'operator', label: 'Operator' }, { value: 'supervisor', label: 'Supervisor' }]} />
            </Field>
            <Field label="Phone"><TextInput testId="op-phone" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0400 000 000" /></Field>
            <Field label="Email"><TextInput testId="op-email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Supplier / Company">
              <SelectInput testId="op-supplier" value={form.supplier_id || ''} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
                placeholder="Select supplier" options={suppliers.map((s) => ({ value: s.id, label: s.company_name }))} />
            </Field>
            <Field label="Licence number"><TextInput testId="op-license" value={form.license_number || ''} onChange={(e) => setForm({ ...form, license_number: e.target.value })} /></Field>
            <Field label="Licence expiry"><DatePicker value={form.license_expiry || ''} onChange={(d) => setForm({ ...form, license_expiry: d })} className="w-full bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" testId="op-license-expiry" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Suburb"><TextInput testId="op-suburb" value={form.suburb || ''} onChange={(e) => setForm({ ...form, suburb: e.target.value })} /></Field>
              <Field label="State">
                <SelectInput testId="op-state" value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })}
                  placeholder="—" options={STATES.map((s) => ({ value: s, label: s }))} />
              </Field>
            </div>
            <Toggle label="Available for allocation" checked={form.is_available} onChange={(v) => setForm({ ...form, is_available: v })} testId="op-available" />
            {workerTypes.length > 0 && (
              <Field label="Worker types">
                <div className="flex flex-wrap gap-2 py-1">
                  {workerTypes.map((wt) => {
                    const sel = (form.worker_type_ids || []).includes(wt.id);
                    return (
                      <button
                        key={wt.id}
                        type="button"
                        onClick={() => setForm((f) => ({
                          ...f,
                          worker_type_ids: sel ? f.worker_type_ids.filter((id) => id !== wt.id) : [...(f.worker_type_ids || []), wt.id],
                        }))}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          sel
                            ? 'border-[#ffd108] bg-[#ffd108]/20 text-[#ffd108]'
                            : 'border-[var(--m-border-light)] bg-[var(--m-bg)] text-[var(--m-text-secondary)]'
                        }`}
                        data-testid={`op-wt-${wt.id}`}
                      >
                        {wt.name}
                      </button>
                    );
                  })}
                </div>
              </Field>
            )}
          </div>
          <div className="space-y-2 pb-2">
            <Button className="btn-volt w-full" onClick={save} disabled={saving} data-testid="op-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save changes' : 'Add operator')}
            </Button>
            {editing && (
              confirmDel ? (
                <Button variant="destructive" className="w-full" onClick={remove} data-testid="op-delete-confirm">Tap again to confirm delete</Button>
              ) : (
                <Button variant="outline" className="w-full border-red-800 text-red-400" onClick={() => setConfirmDel(true)} data-testid="op-delete">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              )
            )}
          </div>
        </SheetContent>
      </Sheet>
    </MobileLayout>
  );
};

export default MobileManageOperators;
